import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

export default function MessagesPage() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(response.data);
    } catch (error) {
      toast.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchMessages = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversation/${userId}`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to fetch messages');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && conversations.length > 0) {
      const conv = conversations.find(c => c.user_id === userId);
      if (conv) {
        setSelectedUser(conv);
        fetchMessages(userId);
      }
    }
  }, [searchParams, conversations, fetchMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(
        `${API_URL}/messages`,
        {
          receiver_id: selectedUser.user_id,
          content: newMessage
        },
        { headers: getAuthHeader() }
      );
      setNewMessage('');
      fetchMessages(selectedUser.user_id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedUser(conv);
    fetchMessages(conv.user_id);
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-white mb-8" data-testid="messages-page-title">
          {t('messages.title')}
        </h1>

        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden" style={{ height: '600px' }}>
          <div className="grid grid-cols-3 h-full">
            {/* Conversations List */}
            <div className="col-span-1 border-r border-[#1E293B]">
              <div className="p-4 border-b border-[#1E293B]">
                <h2 className="font-bold text-white">{t('messages.title')}</h2>
              </div>
              <ScrollArea className="h-[calc(100%-65px)]">
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-10 px-4">
                    <MessageSquare className="w-12 h-12 text-[#1E293B] mx-auto mb-3" />
                    <p className="text-[#94A3B8] text-sm">{t('messages.empty')}</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.user_id}
                      onClick={() => handleSelectConversation(conv)}
                      data-testid={`conversation-${conv.user_id}`}
                      className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-[#0B1120] transition-colors ${
                        selectedUser?.user_id === conv.user_id ? 'bg-amber-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white">{conv.user_name}</h3>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#F59E0B] text-white text-xs px-2 py-1 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#94A3B8] truncate">{conv.last_message}</p>
                    </div>
                  ))
                )}
              </ScrollArea>
            </div>

            {/* Messages Area */}
            <div className="col-span-2 flex flex-col">
              {selectedUser ? (
                <>
                  <div className="p-4 border-b border-[#1E293B]">
                    <h2 className="font-bold text-white" data-testid="selected-user-name">
                      {selectedUser.user_name}
                    </h2>
                  </div>

                  <ScrollArea className="flex-1 p-4" data-testid="messages-area">
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${message.id}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg px-4 py-3 ${
                              message.sender_id === user.id
                                ? 'bg-[#F59E0B] text-white'
                                : 'bg-[#1A2440] text-white'
                            }`}
                          >
                            <p className="text-sm">{message.content}</p>
                            <p className={`text-xs mt-1 ${
                              message.sender_id === user.id ? 'text-amber-100' : 'text-[#64748B]'
                            }`}>
                              {new Date(message.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <form onSubmit={handleSendMessage} className="p-4 border-t border-[#1E293B]">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('messages.placeholder')}
                        data-testid="message-input"
                        className="flex-1 border-[#1E293B]"
                      />
                      <Button
                        type="submit"
                        data-testid="send-message-button"
                        className="bg-[#F59E0B] hover:bg-[#D97706]"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                    <p className="text-[#94A3B8]">{t('messages.empty')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}